<script setup>
/**
 * "You've been asked to speak at this one", and its green twin.
 *
 * One component for both because they are the same panel in two states, and a
 * person moving between them should see a thing change rather than a thing
 * replaced. Amber is a question waiting on them; green is a record of what
 * they already answered.
 *
 * It renders nothing at all for declined, unanswered and never-asked. That is
 * the brief, and it is the part most easily got wrong: a declined session goes
 * back to being an ordinary session, and somebody who never answered is never
 * told anything about it.
 */
import { computed } from 'vue';
import XlCard from '../../xl/XlCard.vue';
import XlButton from '../../xl/XlButton.vue';
import XlStatusIcon from '../../xl/XlStatusIcon.vue';
import IconMicrophone from '../../icons/IconMicrophone.vue';
import { CONSENT, consentRecordLine } from '../../../helpers/speakerConsent';

const props = defineProps({
  /** `{ state, accepted_at, notice_version }` from the session payload. */
  speaking: { type: Object, default: null },
  /** What a speaker sees, given the format. Rewrites itself when it changes. */
  speakerSees: { type: String, default: '' },
  /** Hides the route-in when the panel is already on the accept screen. */
  showAction: { type: Boolean, default: true },
});

const emit = defineEmits(['open', 'see-notice']);

const state = computed(() => props.speaking?.state ?? null);

const asked = computed(() => state.value === CONSENT.PENDING);
const accepted = computed(() => state.value === CONSENT.ACCEPTED);

const record = computed(() => consentRecordLine(
  props.speaking?.accepted_at,
  props.speaking?.notice_version,
));
</script>

<template>
  <XlCard v-if="asked" padded tone="warning" class="speaking-panel" data-testid="speaking-panel-asked">
    <XlStatusIcon tone="warning" glyph="23">
      <IconMicrophone color="currentColor" />
    </XlStatusIcon>
    <div class="speaking-panel__text">
      <b>You've been asked to speak at this one</b>
      <span>It's recorded, so there's something to read before you say yes.</span>
    </div>
    <XlButton v-if="props.showAction" data-testid="speaking-panel-open" @click="emit('open')">
      Read the invitation
    </XlButton>
  </XlCard>

  <XlCard v-else-if="accepted" padded tone="success" class="speaking-panel speaking-panel--block" data-testid="speaking-panel-speaking">
    <div class="speaking-panel__row">
      <XlStatusIcon tone="success" glyph="23">
        <IconMicrophone color="currentColor" />
      </XlStatusIcon>
      <div class="speaking-panel__text">
        <b>You're speaking at this session</b>
        <span>
          Your microphone, camera and screen-share are on for this session only — nothing changes
          anywhere else in the portal.
        </span>
      </div>
    </div>

    <div class="speaking-panel__record">
      <!-- Their own consent record, shown back to them. A record somebody
           cannot read is not a record they can rely on. -->
      <p v-if="record" data-testid="speaking-panel-record">{{ record }}</p>
      <p v-if="props.speakerSees" data-testid="speaking-panel-sees">
        Because of the format, you'll see {{ props.speakerSees }}.
      </p>
    </div>

    <div class="speaking-panel__actions">
      <XlButton variant="ghost" size="sm" data-testid="speaking-panel-notice" @click="emit('see-notice')">
        See what you were sent
      </XlButton>
    </div>
  </XlCard>
</template>

<style scoped>
.speaking-panel {
  display: flex;
  gap: 14px;
  align-items: center;
  flex-wrap: wrap;
  font-family: var(--xl-font-family);
}

.speaking-panel--block { display: block; }

.speaking-panel__row {
  display: flex;
  gap: 14px;
  align-items: center;
  flex-wrap: wrap;
}

.speaking-panel__text {
  flex: 1;
  min-width: 220px;
}

.speaking-panel__text b {
  display: block;
  font-size: 15.5px;
  color: var(--xl-color-on-surface);
}

.speaking-panel__text span {
  font-size: 13.5px;
  line-height: 1.6;
  color: var(--xl-color-on-surface-variant);
}

.speaking-panel__record {
  margin-top: 14px;
  padding-top: 14px;
  border-top: 1px solid var(--xl-color-border);
  font-size: 13px;
  line-height: 1.6;
  color: var(--xl-color-on-surface-variant);
}

.speaking-panel__record p { margin: 0; }

.speaking-panel__actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: 13px;
}
</style>
